import React, { useEffect, useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { List, X } from "@phosphor-icons/react";
import { useAuth } from "@/auth/AuthContext";

const navItems = [
  ["Home", "/"],
  ["About", "/about"],
  ["Contact", "/contact"],
  ["Privacy", "/privacy"],
  ["Terms", "/terms"],
];

export function PageMeta({ title, description }) {
  useEffect(() => {
    document.title = title;
    const meta = document.querySelector('meta[name="description"]');
    if (meta) meta.setAttribute("content", description);
  }, [title, description]);
  return null;
}

export default function MarketingLayout({ children }) {
  const { user, pinLocked } = useAuth();
  const [menuOpen, setMenuOpen] = useState(false);
  const hasSession = Boolean(user || pinLocked);

  return (
    <div className="marketing-site min-h-[100dvh]">
      <a className="marketing-skip-link" href="#main-content">Skip to content</a>
      <header className="marketing-nav sticky top-0 z-40">
        <div className="mx-auto flex h-[72px] max-w-7xl items-center justify-between gap-6 px-4 sm:px-6 lg:px-8">
          <Link to="/" className="flex min-w-0 items-center gap-3" aria-label="Nothing Before Law home">
            <img src="/logo-nbl-icon.svg" alt="" className="h-10 w-10 shrink-0" />
            <span className="leading-tight">
              <span className="block font-display text-xl text-[var(--m-text)]">Nothing Before Law</span>
              <span className="block text-[11px] font-semibold tracking-[0.08em] text-[var(--m-muted)]">CASE DIARY</span>
            </span>
          </Link>

          <nav className="hidden items-center gap-1 lg:flex" aria-label="Primary navigation">
            {navItems.map(([label, to]) => (
              <NavLink
                key={to}
                to={to}
                end={to === "/"}
                className={({ isActive }) => `marketing-nav-link ${isActive ? "is-active" : ""}`}
              >
                {label}
              </NavLink>
            ))}
          </nav>

          <div className="hidden items-center gap-2 lg:flex">
            {hasSession ? (
              <Link to="/dashboard" className="marketing-primary-button">Open diary</Link>
            ) : (
              <>
                <Link to="/login" className="marketing-secondary-button">Sign in</Link>
                <Link to="/login?mode=register" className="marketing-primary-button">Create account</Link>
              </>
            )}
          </div>

          <button
            type="button"
            className="marketing-icon-button lg:hidden"
            aria-label={menuOpen ? "Close navigation" : "Open navigation"}
            aria-expanded={menuOpen}
            onClick={() => setMenuOpen((open) => !open)}
          >
            {menuOpen ? <X size={22} /> : <List size={24} />}
          </button>
        </div>

        {menuOpen && (
          <nav className="marketing-mobile-menu lg:hidden" aria-label="Mobile navigation">
            <div className="mx-auto max-w-7xl px-4 py-4 sm:px-6">
              {navItems.map(([label, to]) => (
                <NavLink
                  key={to}
                  to={to}
                  end={to === "/"}
                  onClick={() => setMenuOpen(false)}
                  className={({ isActive }) => `marketing-mobile-link ${isActive ? "is-active" : ""}`}
                >
                  {label}
                </NavLink>
              ))}
              <div className="mt-3 grid grid-cols-2 gap-2 border-t border-[var(--m-border)] pt-4">
                {hasSession ? (
                  <Link to="/dashboard" onClick={() => setMenuOpen(false)} className="marketing-primary-button col-span-2">Open diary</Link>
                ) : (
                  <>
                    <Link to="/login" onClick={() => setMenuOpen(false)} className="marketing-secondary-button">Sign in</Link>
                    <Link to="/login?mode=register" onClick={() => setMenuOpen(false)} className="marketing-primary-button">Create account</Link>
                  </>
                )}
              </div>
            </div>
          </nav>
        )}
      </header>

      <main id="main-content">{children}</main>

      <footer className="marketing-footer">
        <div className="mx-auto grid max-w-7xl gap-10 px-4 py-12 sm:px-6 md:grid-cols-[1.4fr_1fr_1fr] lg:px-8">
          <div className="max-w-sm">
            <div className="flex items-center gap-3">
              <img src="/logo-nbl-icon.svg" alt="" className="h-9 w-9" />
              <span className="font-display text-xl text-[var(--m-text)]">Nothing Before Law</span>
            </div>
            <p className="mt-4 text-sm leading-6 text-[var(--m-muted)]">
              A focused case diary for Indian advocates, chambers and growing legal teams.
            </p>
          </div>
          <div>
            <h2 className="text-sm font-semibold text-[var(--m-text)]">Explore</h2>
            <div className="mt-4 grid gap-3 text-sm text-[var(--m-muted)]">
              <Link className="marketing-footer-link" to="/about">About</Link>
              <Link className="marketing-footer-link" to="/login">Sign in</Link>
              <Link className="marketing-footer-link" to="/login?mode=register">Create account</Link>
            </div>
          </div>
          <div>
            <h2 className="text-sm font-semibold text-[var(--m-text)]">Legal</h2>
            <div className="mt-4 grid gap-3 text-sm text-[var(--m-muted)]">
              <Link className="marketing-footer-link" to="/privacy">Privacy Policy</Link>
              <Link className="marketing-footer-link" to="/terms">Terms and Conditions</Link>
              <Link className="marketing-footer-link" to="/refund-policy">Refund Policy</Link>
              <Link className="marketing-footer-link" to="/contact">Contact Us</Link>
            </div>
          </div>
        </div>
        <div className="border-t border-[var(--m-border)] px-4 py-5 text-center text-xs text-[var(--m-muted)]">
          © {new Date().getFullYear()} Nothing Before Law. All rights reserved.
        </div>
      </footer>
    </div>
  );
}
